import React, {useEffect, useMemo, useState} from "react";
import {collection, getDocs, query, where} from "@firebase/firestore";
import {Card} from "react-bootstrap";
import {db} from "../../firebase/FirebaseProvider";
import {Stay, Session, User} from "../types/Types";
import LeaderboardNav from "./LeaderboardNav";
import UserRow from "./UserRow";
import LeaderboardV2Rows from "./LeaderboardV2Rows";
import {V2GlassPanel} from "../shared/v2/V2Glass";
import V2YearPicker from "../shared/v2/V2YearPicker";
import {addNumberSuffix} from "../util/Util";
import PageTracking from "../auth/PageTracking";

interface Props {
    users: Array<User>;
    currentUser: User | undefined;
    sessionsMap: Map<string, Session>;
}

interface RankedStayUser {
    user: User;
    stays: number;
}

function getStayYear(value: any): number {
    if (value?.seconds) {
        return new Date(value.seconds * 1000).getFullYear();
    }
    if (value?.toDate) {
        return value.toDate().getFullYear();
    }
    return new Date(value).getFullYear();
}

function LeaderboardStays(props: Props) {
    const [stays, setStays] = useState<Array<Stay> | undefined>(undefined);
    const [year, setYear] = useState<string>("1");

    useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                const staysSnapshot = await getDocs(query(
                    collection(db, "stays"),
                    where("stayed", "==", true),
                ));
                if (isMounted) {
                    setStays(staysSnapshot.docs.map((stayDoc) => stayDoc.data() as Stay));
                }
            } catch (error) {
                console.error("Failed to load stays leaderboard", error);
                if (isMounted) {
                    setStays([]);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, []);

    const usersById = useMemo(() => {
        const map = new Map(props.users.map((user) => [user.id, user]));
        if (props.currentUser && !props.currentUser.socialDisabled) {
            map.set(props.currentUser.id, props.currentUser);
        }
        return map;
    }, [props.currentUser, props.users]);

    const availableYears = useMemo(() => {
        if (!stays) {
            return [];
        }

        return Array.from(new Set(stays.map((stay) => getStayYear(stay.lastModified))));
    }, [stays]);

    const rankedUsers = useMemo<Array<RankedStayUser> | undefined>(() => {
        if (!stays) {
            return undefined;
        }

        const locationsByUser = new Map<string, Set<string>>();
        stays
            .filter((stay) => year === "1" || getStayYear(stay.lastModified) === Number(year))
            .forEach((stay) => {
                if (!locationsByUser.has(stay.userId)) {
                    locationsByUser.set(stay.userId, new Set<string>());
                }
                locationsByUser.get(stay.userId)?.add(stay.locationId);
            });

        return Array.from(locationsByUser.entries())
            .map(([userId, locationIds]) => ({
                user: usersById.get(userId),
                stays: locationIds.size,
            }))
            .filter((entry): entry is RankedStayUser => !!entry.user && !entry.user.socialDisabled && entry.stays > 0)
            .sort((first, second) => second.stays - first.stays || first.user.name.localeCompare(second.user.name));
    }, [stays, usersById, year]);

    const currentUserRank = rankedUsers?.findIndex((entry) => entry.user.id === props.currentUser?.id) ?? -1;
    const currentUserStays = currentUserRank >= 0 ? rankedUsers?.[currentUserRank].stays ?? 0 : 0;

    return (
        <>
            <PageTracking />
            {props.currentUser && <>
                <LeaderboardNav currentUser={props.currentUser} />
                {props.currentUser.enableUiV2 ? <V2GlassPanel className="leaderboard-v2-list-card v2-glass-list-panel">
                    <div className="leaderboard-v2-year-header">
                        <div className="leaderboard-v2-summary">
                            {!rankedUsers && <span>Calculating...</span>}
                            {rankedUsers && currentUserRank >= 0 && <span>You&apos;re ranked <b>{currentUserRank + 1}{addNumberSuffix(currentUserRank + 1)}</b> out of <b>{rankedUsers.length}</b></span>}
                            {rankedUsers && currentUserRank < 0 && <span>{currentUserStays > 0 ? "You’re not ranked yet." : "You haven’t recorded any stays yet."}</span>}
                        </div>
                        <V2YearPicker
                            value={year}
                            onChange={setYear}
                            availableYears={availableYears}
                            ariaLabel="Select stays year"
                        />
                    </div>
                    {rankedUsers && <LeaderboardV2Rows
                        rows={rankedUsers.map((entry) => ({
                            user: entry.user,
                            value: entry.stays,
                            session: props.sessionsMap.get(entry.user.id),
                        }))}
                        linksToTab="stays"
                        emptyMessage="No stays recorded yet."
                    />}
                </V2GlassPanel> : rankedUsers && <Card className="mb-3">
                    <Card.Header>
                        {currentUserRank >= 0 && <div>You&apos;re ranked: {currentUserRank + 1}<small>{addNumberSuffix(currentUserRank + 1)}</small> out of {rankedUsers.length}</div>}
                        {currentUserRank < 0 && <div>You haven&apos;t recorded any stays yet.</div>}
                    </Card.Header>
                    <Card.Body style={{paddingTop: "0.4em"}}>
                        {rankedUsers.map((entry, index) => (
                            <UserRow
                                key={entry.user.id}
                                index={index}
                                linksToTab="stays"
                                countToDisplay={entry.stays.toString()}
                                user={entry.user}
                                session={props.sessionsMap.get(entry.user.id)}
                            />
                        ))}
                        {rankedUsers.length === 0 && <div className="loading">No stays recorded yet.</div>}
                    </Card.Body>
                </Card>}
            </>}
        </>
    );
}

export default LeaderboardStays;
